import React, {
    useState,
    useEffect,
    useRef,
    useImperativeHandle,
    forwardRef
} from 'react';
import PropTypes from 'prop-types';
import {
    isEqual as _isEqual,
    cloneDeep as _cloneDeep
} from 'lodash';
import TextField from '@mui/material/Input';
import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Checkbox from '@mui/material/Checkbox';
import InputAdornment from '@mui/material/InputAdornment';

import Filter from './filter';
import Validator, { useValidate, checkValidity } from '../validator';

import SystemService from '../../data/system-service';

const useStyles = makeStyles(theme => ({
    fullWidth: {
        width: '100%',
    },
    timezone: {
        width: '120px',
    },
    hideFileInput: {
        display: 'none',
    },
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    codemirrorBox: {
        display: 'block',
        width: '100%',
    },
}));

const ES = forwardRef((props, ref) => {
    const classes = useStyles();
    const [systemConstants, setSystemConstants] = useState({
        facilities: [],
        severities: [],
        timezones: [],
    });
    const filterRef = useRef(null);
    const [init, setInit] = useState(true);
    const [original, setOriginal] = useState(_cloneDeep(props.data));
    const [data, setData] = useState(_cloneDeep(props.data));
    const [, setChanges] = useState({});
    const dataForm = useValidate({});

    useImperativeHandle(ref, () => ({
        getData: () => {
            const f = filterRef.current.getFilter();
            return {
                ...data,
                filterconf: f,
            };
        },
    }));

    useEffect(() => {
        SystemService.getConstants().then((res) => {
            setSystemConstants(res);
        });
    }, []);

    useEffect(() => {
        const d = { ...props.data };
        if (d.prune_type === '') {
            d.prune_type = 'inactive';
        }

        if (init) {
            setOriginal(_cloneDeep(d));
            setData(_cloneDeep(d));
            setInit(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props]);

    useEffect(() => {
        const t = !_isEqual(data, original);
        setChanges(t);
        if (t) {
            props.onChange(props.name, t, data);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        if (props.onUpdateForm) {
            props.onUpdateForm(dataForm);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataForm]);

    const handleDest = (e, validID, valid) => {
        dataForm.set({ ...dataForm.get(), [validID]: valid });

        const d = { ...data };
        d.dest.url = e.target.value;
        setData(d);
    };

    const handleDestUser = (e, validID, valid) => {
        dataForm.set({ ...dataForm.get(), [validID]: valid });
        const d = { ...data };
        d.dest.user = e.target.value;
        setData(d);
    };

    const handleDestPassword = (e) => {
        const d = { ...data };
        d.dest.password = e.target.value;
        setData(d);
    };

    const handleIndex = (e, validID, valid) => {
        dataForm.set({ ...dataForm.get(), [validID]: valid });

        const d = { ...data };
        d.elk.index = e.target.value.toLowerCase();
        setData(d);
    };

    const handleRotation = (e, validID, valid) => {
        dataForm.set({ ...dataForm.get(), [validID]: valid });

        const d = { ...data };
        d.elk.index_rotate_days = parseInt(e.target.value);
        setData(d);
    };

    // const handlePruneTypeChange = (e) => {
    //     const d = { ...data };
    //     d.prune_type = e.target.value;
    //     setData(d);
    // };

    // const handlePruneKeysChange = (e, validID, valid) => {
    //     dataForm.set({ ...dataForm.get(), [validID]: valid });

    //     const d = { ...data };
    //     d.prune_keys = e.target.value ? e.target.value.replace(/\s/g, '').split(',') : [];
    //     setData(d);
    // };

    const handleTimezone = (e, comp) => {
        const d = { ...data };
        d.elk.dst_tz = comp.props.value;
        setData(d);
    };

    const handleECSEnable = (e) => {
        const d = { ...data };
        d.elk.ecs.enabled = e.currentTarget.checked;
        setData(d);
    };

    const handleECSVersion = (e) => {
        const d = { ...data };
        d.elk.ecs.version = e.target.value;
        setData(d);
    };

    const handleUpdateFilter = (filter) => {
        console.log('Update Filter', filter);
    };

    const handleVerify = (k, i, close) => {
        setInit(true);
        props.onVerify(k, i, close);
    };

    const handleCancel = (k, i) => {
        setInit(true);
        setData(original);
        props.onCancel(k, i);
    };

    const handleEventTypeAlerts = (e) => {
        const d = { ...data };
        if (e.currentTarget.checked) {
            d.event_types.push('alert');
        } else {
            d.event_types.splice(d.event_types.indexOf('alert'), 1);
        }
        setData(d);
    };

    const handleEventTypeAnomalies = (e) => {
        const d = { ...data };
        if (e.currentTarget.checked) {
            d.event_types.push('anomaly');
        } else {
            d.event_types.splice(d.event_types.indexOf('anomaly'), 1);
        }
        setData(d);
    };

    const handleEventTypeMetadata = (e) => {
        const d = { ...data };
        if (e.currentTarget.checked) {
            d.event_types.push('bro_log');
        } else {
            d.event_types.splice(d.event_types.indexOf('bro_log'), 1);
        }
        setData(d);
    };

    const handleEventTypeDPIMetadata = (e) => {
        const d = { ...data };
        if (e.currentTarget.checked) {
            d.event_types.push('dpi');
        } else {
            d.event_types.splice(d.event_types.indexOf('dpi'), 1);
        }
        setData(d);
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Box>
                    <Box display="inline-block" pr={5}>Event Type(s):</Box>
                    <FormControlLabel
                        label="Alerts"
                        onChange={(e) => { handleEventTypeAlerts(e) }}
                        control={<Checkbox disabled={(!props.perms.write || props.policySource === 'mc2')} checked={data.event_types.indexOf('alert') !== -1} />}
                    />
                    {props.cmc && <FormControlLabel
                        label="Anomalies"
                        onChange={(e) => { handleEventTypeAnomalies(e) }}
                        control={<Checkbox disabled={(!props.perms.write || props.policySource === 'mc2')} checked={data.event_types.indexOf('anomaly') !== -1} />}
                    />}
                    <FormControlLabel
                        label="Metadata"
                        onChange={(e) => { handleEventTypeMetadata(e) }}
                        control={<Checkbox disabled={(!props.perms.write || props.policySource === 'mc2')} checked={data.event_types.indexOf('bro_log') !== -1} />}
                    />
                    <FormControlLabel
                        label="DPI Metadata"
                        onChange={(e) => { handleEventTypeDPIMetadata(e) }}
                        control={<Checkbox disabled={(!props.perms.write || props.policySource === 'mc2')} checked={data.event_types.indexOf('dpi') !== -1} />}
                    />
                </Box>
            </Grid>
            <Grid item xs={6}>
                <Box>
                    <FormControl className={classes.fullWidth}>
                        <FormLabel id={'audit-destination-label-' + props.index}>Destination URL</FormLabel>
                        <Validator
                            disabled={(!props.perms.write || props.policySource === 'mc2')}
                            errorText="Destination URL cannot be empty"
                            fullWidth
                            value={data.dest.url}
                            onChange={handleDest}
                        />
                    </FormControl>
                </Box>
                <Box mt={3}>
                    <FormControl className={classes.fullWidth}>
                        <FormLabel id={'audit-destination-label-' + props.index}>User</FormLabel>
                        <Validator
                            valueType="username"
                            disabled={(!props.perms.write || props.policySource === 'mc2')}
                            fullWidth
                            variant="outlined"
                            allowEmpty={true}
                            value={data.dest.user || ''}
                            onChange={handleDestUser}
                        />
                    </FormControl>
                </Box>
                <Box mt={3}>
                    <FormControl className={classes.fullWidth}>
                        <FormLabel id={'audit-destination-label-' + props.index}>Password</FormLabel>
                        <Validator
                            valueType="username"
                            disabled={(!props.perms.write || props.policySource === 'mc2')}
                            type="password"
                            variant="outlined"
                            allowEmpty={true}
                            fullWidth
                            value={data.dest.password || ''}
                            onChange={handleDestPassword}
                        />
                    </FormControl>
                </Box>
                <Box mt={3}>
                    <FormControl className={classes.fullWidth}>
                        <FormLabel id={'audit-index-label-' + props.index}>Index</FormLabel>
                        <Validator
                            valueType="index"
                            errorText="Index value in invalid"
                            allowEmpty
                            disabled={(!props.perms.write || props.policySource === 'mc2')}
                            fullWidth
                            value={data.elk.index}
                            onChange={handleIndex}
                        />
                    </FormControl>
                </Box>
                <Box mt={3}>
                    <FormControl className={classes.fullWidth}>
                        <FormLabel id={'audit-rotation-label-' + props.index}>Rotation</FormLabel>
                        <Validator
                            disabled={(!props.perms.write || props.policySource === 'mc2')}
                            valueType="range"
                            errorText="Rotation must be within 0 and 365 days"
                            type="number"
                            inputProps={{
                                min: 0,
                                max: 365
                            }}
                            fullWidth
                            endAdornment={<InputAdornment position='end'>Days</InputAdornment>}
                            value={data.elk.index_rotate_days}
                            onChange={handleRotation}
                        />
                    </FormControl>
                </Box>
                {/* <Box mt={3}>
                    <Typography>Event Pruning Type:</Typography>
                    <RadioGroup row value={data.prune_type} onChange={handlePruneTypeChange}>
                        <FormControlLabel
                            disabled={(!props.perms.write || props.policySource === 'mc2')}
                            label="Inactive"
                            aria-label="inactive"
                            control={<Radio value='inactive' />} />
                        <FormControlLabel
                            disabled={(!props.perms.write || props.policySource === 'mc2')}
                            label="Remove"
                            aria-label="remove"
                            control={<Radio value='black' />} />
                        <FormControlLabel
                            disabled={(!props.perms.write || props.policySource === 'mc2')}
                            label="Add"
                            aria-label="add"
                            control={<Radio value='white' />} />
                    </RadioGroup>
                </Box>
                <Box mt={3}>
                    <FormControl className={classes.fullWidth}>
                        <FormLabel>Event Pruning Keys:</FormLabel>
                        <Validator
                            disabled={(!props.perms.write || props.policySource === 'mc2')}
                            allowEmpty
                            valueType="keyPairList"
                            errorText="Must be valid comma separated key pairs ex. bricata:dest_location"
                            fullWidth
                            value={data.prune_keys ? data.prune_keys.join(',') : []}
                            onChange={handlePruneKeysChange}
                        />
                    </FormControl>
                </Box> */}
            </Grid>
            <Grid item xs={6}>
                <Box>
                    <FormControl className={classes.fullWidth}>
                        <FormLabel id={'es-tz-label-' + props.index}>Destination Time Zone</FormLabel>
                        <Select disabled={(!props.perms.write || props.policySource === 'mc2')} className={classes.fullWidth} id={'es-tz-' + props.index} value={data.elk.dst_tz} onChange={handleTimezone}>
                            {systemConstants.timezones.map((v, i) => {
                                return <MenuItem key={'timezone-' + i} value={v.name}>{v.utc_offset} - {v.name}</MenuItem>;
                            })}
                        </Select>
                    </FormControl>
                </Box>
                <Box mt={3}>
                    <FormControlLabel
                        disabled={(!props.perms.write || props.policySource === 'mc2')}
                        label="ECS"
                        control={<Checkbox checked={data.elk.ecs.enabled} />}
                        onChange={handleECSEnable}
                    />
                </Box>
                <Box mt={3}>
                    <FormControl className={classes.fullWidth}>
                        <FormLabel id={'audit-protocol-label-' + props.index}>ECS Version</FormLabel>
                        <Select
                            className={classes.fullWidth}
                            disabled={(!props.perms.write || props.policySource === 'mc2') || !data.elk.ecs.enabled}
                            value={data.elk.ecs.version}
                            onChange={handleECSVersion}
                        >
                            <MenuItem value="8.2.1">8.2.1</MenuItem>
                            <MenuItem value="1.2.0">1.2.0</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Grid>
            <Filter
                filter={data.filterconf}
                ref={filterRef}
                onUpdate={handleUpdateFilter}
                onVerify={() => { handleVerify(props.name, props.index, false) }}
                policySource={props.policySource}
                perms={props.perms}
            />
            {(props.perms.write || props.policySource !== 'mc2') && <Grid item xs={12}>
                <Box mt={2}>
                    <Button
                        className={classes.buttonRight}
                        variant="contained"
                        color="primary"
                        aria-label="save"
                        onClick={() => { handleVerify(props.name, props.index, true) }}
                        disabled={!(checkValidity(dataForm.get()))}
                    >Verify</Button>
                    <Button className={classes.buttonRight} variant="contained" color="secondary" aria-label="cancel" onClick={() => { handleCancel(props.name, props.index) }}>Cancel</Button>
                </Box>
            </Grid>}
        </Grid>
    );
});

ES.propTypes = {
    data: PropTypes.object.isRequired,
    policySource: PropTypes.string,
    name: PropTypes.string.isRequired,
    index: PropTypes.number.isRequired,
    onCancel: PropTypes.func.isRequired,
    onVerify: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    onUpdateForm: PropTypes.func,
    perms: PropTypes.object,
    cmc: PropTypes.bool,
};

export default ES;
